/** Keeps bookmarked /reports/:reportId links working after consolidation.
 *
 *  Reports used to have a detail page of their own, built before the theme
 *  tokens and the shared layout components, and showing only overview, data
 *  sources and change history. Every artifact type is now served by
 *  ArtifactDetailPage, which additionally shows access, access changes, public
 *  exposure, subscriptions and refresh history — so keeping the old page would
 *  have meant two report views that disagree about what a report is.
 *
 *  The new route is workspace-scoped, but a bookmarked link only carries the
 *  report id, so the workspace has to be looked up before redirecting. That is
 *  what this exists for: one request, then a replace-navigation so the dead URL
 *  does not sit in the back-button history.
 */
import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import apiClient from "../api/client";
import type { ReportDetail } from "../types";
import ErrorBanner from "../components/ErrorBanner";
import PageHeader from "../components/PageHeader";
import { LoadingPanel } from "../components/Spinner";

export default function ReportRedirectPage() {
  const { reportId = "" } = useParams<{ reportId: string }>();
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!reportId) return;
    apiClient
      .get<ReportDetail>(`/api/reports/${reportId}`)
      .then((res) => setWorkspaceId(res.data.workspaceId))
      .catch((e) => setError(e?.message ?? "Report not found."));
  }, [reportId]);

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Report"
          breadcrumb={[{ label: "Reports", to: "/reports" }, { label: reportId }]}
        />
        <ErrorBanner message={error} />
      </div>
    );
  }

  if (!workspaceId) return <LoadingPanel label="Opening report…" />;

  return (
    <Navigate
      replace
      to={`/workspaces/${workspaceId}/artifacts/report/${encodeURIComponent(reportId)}`}
    />
  );
}
